import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AppIcons } from 'app/modules/shared/app.icons';
import { AppSettings } from 'app/modules/shared/app.settings';
import { ModuleHeaderComponent } from 'app/modules/shared/components/module-header/module-header.component';
import { CommonBindingDataService } from 'app/modules/shared/services/common-binding-data.service';
import { ConfigService } from 'app/modules/shared/services/config.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { LoaderService } from 'app/modules/shared/components/loader/loader.service';
import { DownloadService } from 'app/modules/shared/services/download.service';
import { MessageService, MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { TabViewModule } from 'primeng/tabview';

@Component({
  selector: 'app-order-summary-report',
  standalone: true,
  imports: [
    BreadcrumbModule,
    ButtonModule,
    CalendarModule,
    CommonModule,
    DropdownModule,
    MultiSelectModule,
    ModuleHeaderComponent,
    ReactiveFormsModule,
    TabViewModule,
    TranslateModule
  ],
  templateUrl: './order-summary-report.component.html',
  styleUrl: './order-summary-report.component.scss'
})
export class OrderSummaryReportComponent implements OnInit {

  miIcons = AppIcons;
  routePath: MenuItem[] = [];
  filterForm: FormGroup;
  protocolOptions: any[] = [];
  siteOptions: any[] = [];
  isDownloading: boolean = false;
  showImportExportBtn: boolean = false;
  maxDate: Date = new Date();

  constructor(
    private fb: FormBuilder,
    private cs: CommonBindingDataService,
    private entityService: EntityService,
    private configService: ConfigService,
    private loaderService: LoaderService,
    private downloadService: DownloadService,
    private messageService: MessageService
  ) {
    this.initializeForm();
  }

  ngOnInit() {
    this.initializeLabels();
    this.loadProtocols();
    this.loadSites();
  }

  private initializeForm() {
    const currentMonthRange = this.getCurrentMonthDateRange();
    
    this.filterForm = this.fb.group({
      protocolId: [[]],
      siteId: [[]],
      orderCreationDateRange: [currentMonthRange, [this.dateRangeValidator.bind(this)]],
      orderReceivedDateRange: [null, [this.dateRangeValidator.bind(this)]]
    });
  }

  initializeLabels() {
    this.routePath = [{ label: this.cs.getLabel('reports.lbl_aksys_order_report'), routerLink: 'aksys' }];
  }

  /**
   * Get the current month date range (first day of current month to today)
   */
  private getCurrentMonthDateRange(): Date[] {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    // First day of current month
    const firstDay = new Date(year, month, 1);
    
    // Today
    const today = new Date();
    
    return [firstDay, today];
  }

  /**
   * Custom validator for date range to ensure maximum 31 days period
   */
  private dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const dateRange = control.value;
    
    if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) {
      return null; // No validation error for empty or incomplete range
    }

    const [startDate, endDate] = dateRange;
    
    if (!startDate || !endDate) {
      return null; // No validation error for incomplete range
    }

    const daysDifference = this.calculateDaysDifference(startDate, endDate);
    
    if (daysDifference > 31) {
      return { 
        dateRangeExceeded: { 
          maxDays: 31, 
          actualDays: daysDifference,
          message: this.cs.getLabel('reports.date_range_max_31_days') || 'Date range cannot exceed 31 days'
        } 
      };
    }

    return null;
  }

  /**
   * Calculate the difference in days between two dates
   */
  private calculateDaysDifference(startDate: Date, endDate: Date): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Reset time to avoid time zone issues
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    const timeDifference = end.getTime() - start.getTime();
    const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24)) + 1; // +1 to include both start and end date
    
    return daysDifference;
  }

  /**
   * Get start of day (00:00:00) for a given date
   */
  private getStartOfDay(date: Date): Date {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay;
  }

  /**
   * Get end of day (23:59:59.999) for a given date
   */
  private getEndOfDay(date: Date): Date {
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay;
  }

  /**
   * Check if a date range is valid and within the maximum allowed period
   */
  public isDateRangeValid(dateRange: Date[]): boolean {
    if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) {
      return true; // Empty or incomplete range is considered valid
    }

    const [startDate, endDate] = dateRange;
    
    if (!startDate || !endDate) {
      return true; // Incomplete range is considered valid
    }

    return this.calculateDaysDifference(startDate, endDate) <= 31;
  }

  /**
   * Get error message for date range validation
   */
  public getDateRangeErrorMessage(controlName: string): string {
    const control = this.filterForm.get(controlName);
    
    if (control?.errors?.dateRangeExceeded) {
      const error = control.errors.dateRangeExceeded;
      return error.message || `Date range cannot exceed ${error.maxDays} days. Current selection: ${error.actualDays} days.`;
    }
    
    return '';
  }

  /**
   * Check if a specific date range control has validation errors
   */
  public hasDateRangeError(controlName: string): boolean {
    const control = this.filterForm.get(controlName);
    return !!(control?.errors?.dateRangeExceeded && (control.dirty || control.touched));
  }

  private isDateRangeIncomplete(dateRange: Date[]): boolean {
    if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) {
      return false;
    }

    const [startDate, endDate] = dateRange;
    
    return (!!startDate && !endDate) || (!startDate && !!endDate);
  }

  private isFromDateMissing(dateRange: Date[]): boolean {
    if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) {
      return false;
    }

    const [startDate, endDate] = dateRange;
    return !startDate && !!endDate;
  }

  loadProtocols() {
    const payload = {
      limit: 1000,
      offset: 0,
      searchStr: "",
      filters: [],
      advanceFilters: [],
      countryCode: AppSettings.HEADER_COUNTRY_CODE,
      deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
      forTenantCode: this.configService.getForTenantCode(),
      actionStatus: "",
      tableViewCode: "ORDER_RELATION_VIEW"
    };

    this.entityService.searchEntity(AppSettings.ENTITY_CODE.PROTOCOL, payload, false).subscribe({
      next: (res: any) => {
        if (res && res.data) {
          this.protocolOptions = res.data.map((protocol: any) => ({
            label: protocol.values?.protocol_name || protocol.values?.name || 'Unknown Protocol',
            value: protocol.id
          })).sort((a, b) => a.label.localeCompare(b.label));
        }
      },
      error: () => {
        // Error loading protocols
      }
    });
  }

  loadSites() {
    const payload = {
      limit: 1000,
      offset: 0,
      searchStr: "",
      filters: [],
      advanceFilters: [],
      countryCode: AppSettings.HEADER_COUNTRY_CODE,
      deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
      forTenantCode: this.configService.getForTenantCode(),
      actionStatus: "",
      tableViewCode: "ORDER_RELATION_VIEW"
    };

    this.entityService.searchEntity(AppSettings.ENTITY_CODE.SITE, payload, false).subscribe({
      next: (res: any) => {
        if (res && res.data) {
          this.siteOptions = res.data.map((site: any) => ({
            label: site.values?.site_name || site.values?.name || 'Unknown Site',
            value: site.id
          })).sort((a, b) => a.label.localeCompare(b.label));
        }
      },
      error: () => {
        // Error loading sites
      }
    });
  }

  downloadReport() {
    const formValues = this.filterForm.value;

    if (formValues.orderCreationDateRange) {
      const [startDate, endDate] = formValues.orderCreationDateRange;
      if (startDate && !endDate) {
        this.messageService.add({
          key: 'tst',
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Please enter both From and To dates to proceed.'
        });
        return;
      }
      if (!startDate && endDate) {
        this.messageService.add({
          key: 'tst',
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Please enter both From and To dates to proceed.'
        });
        return;
      }
    }

    if (formValues.orderReceivedDateRange) {
      const [startDate, endDate] = formValues.orderReceivedDateRange;
      if (startDate && !endDate) {
        this.messageService.add({
          key: 'tst',
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Please enter both From and To dates to proceed.'
        });
        return;
      }
      if (!startDate && endDate) {
        this.messageService.add({
          key: 'tst',
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Please enter both From and To dates to proceed.'
        });
        return;
      }
    }

    // Check if form is invalid due to date range validation
    if (this.filterForm.invalid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.filterForm.controls).forEach(key => {
        this.filterForm.get(key)?.markAsTouched();
      });

      // Show specific error message for date range validation
      const hasCreationDateError = this.hasDateRangeError('orderCreationDateRange');
      const hasReceivedDateError = this.hasDateRangeError('orderReceivedDateRange');
      
      if (hasCreationDateError || hasReceivedDateError) {
        this.messageService.add({
          key: 'tst',
          severity: 'error',
          summary: 'Validation Error',
          detail: this.cs.getLabel('reports.date_range_validation_error') || 'Please correct the date range errors before proceeding.'
        });
        return;
      }
    }

    // Validate individual date ranges
    if (!this.isDateRangeValid(formValues.orderCreationDateRange)) {
      this.messageService.add({
        key: 'tst',
        severity: 'error',
        summary: 'Invalid Date Range',
        detail: this.cs.getLabel('reports.date_range_max_31_days') || 'Order creation date range cannot exceed 31 days.'
      });
      return;
    }

    if (!this.isDateRangeValid(formValues.orderReceivedDateRange)) {
      this.messageService.add({
        key: 'tst',
        severity: 'error',
        summary: 'Invalid Date Range',
        detail: this.cs.getLabel('reports.date_range_max_31_days') || 'Order received date range cannot exceed 31 days.'
      });
      return;
    }

    if ((!formValues.protocolId || formValues.protocolId.length === 0) &&
      (!formValues.siteId || formValues.siteId.length === 0) &&
      !formValues.orderCreationDateRange && !formValues.orderReceivedDateRange) {
      this.messageService.add({
        key: 'tst',
        severity: 'warn',
        summary: 'Warning',
        detail: this.cs.getLabel('reports.select_at_least_one_filter')
      });
      return;
    }

    this.isDownloading = true;
    this.loaderService.show();

    const reportPayload = {
      forTenantCode: this.configService.getForTenantCode(),
      orderCreatedStartDate: formValues.orderCreationDateRange?.[0] ?
        this.getStartOfDay(formValues.orderCreationDateRange[0]).getTime() : 0,
      orderCreatedEndDate: formValues.orderCreationDateRange?.[1] ?
        this.getEndOfDay(formValues.orderCreationDateRange[1]).getTime() : 0,
      orderReceivedStartDate: formValues.orderReceivedDateRange?.[0] ?
        this.getStartOfDay(formValues.orderReceivedDateRange[0]).getTime() : 0,
      orderReceivedEndDate: formValues.orderReceivedDateRange?.[1] ?
        this.getEndOfDay(formValues.orderReceivedDateRange[1]).getTime() : 0,
      searchStr: "",
      siteIds: formValues.siteId || [],
      protocolIds: formValues.protocolId || []
    };

    this.entityService.exportOrderAksysReport(AppSettings.ENTITY_CODE.ORDER, reportPayload).subscribe({
      next: (res) => {
        this.loaderService.hide();
        this.isDownloading = false;
        if (res && this.downloadService.isValidResponse(res)) {
          try {
            this.downloadService.downloadZip(res, 'order_summary_report');
            this.messageService.add({
              key: 'tst',
              severity: 'success',
              summary: 'Success',
              detail: this.cs.getLabel('reports.report_downloaded_successfully')
            });
          } catch (error) {
            this.messageService.add({
              key: 'tst',
              severity: 'error',
              summary: 'Error',
              detail: this.cs.getLabel('reports.download_failed')
            });
          }
        } else {
          this.messageService.add({
            key: 'tst',
            severity: 'error',
            summary: 'Error',
            detail: this.cs.getLabel('reports.invalid_response')
          });
        }
      },
      error: () => {
        this.loaderService.hide();
        this.isDownloading = false;
        this.messageService.add({
          key: 'tst',
          severity: 'error',
          summary: 'Error',
          detail: this.cs.getLabel('reports.download_failed')
        });
      }
    });
  }
}
